"use client";

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: string;
}

const facilitiesSteps: ProcessStep[] = [
  {
    id: 'fac_step_1',
    number: '01',
    title: 'Tell us role, pay range, schedule, timeline',
    description: 'Takes 3 minutes. We start sourcing immediately.',
    icon: 'DocumentTextIcon',
  },
  {
    id: 'fac_step_2',
    number: '02',
    title: 'We source and screen',
    description: 'License checks. Reference calls. Culture fit. First candidates in 48 hours.',
    icon: 'MagnifyingGlassIcon',
  },
  {
    id: 'fac_step_3',
    number: '03',
    title: 'You interview top matches',
    description: '3-5 pre-screened candidates. You pick who to interview.',
    icon: 'UserGroupIcon',
  },
  {
    id: 'fac_step_4',
    number: '04',
    title: 'We support offer and start date',
    description: 'Negotiation help. Onboarding coordination. Check-ins through day one.',
    icon: 'CheckCircleIcon',
  },
];

const cliniciansSteps: ProcessStep[] = [
  {
    id: 'clin_step_1',
    number: '01',
    title: 'Apply in 2 minutes',
    description: 'Name, license, specialty, location. That\'s it.',
    icon: 'DocumentCheckIcon',
  },
  {
    id: 'clin_step_2',
    number: '02',
    title: 'Quick call to confirm goals',
    description: 'We call within 24 hours. Discuss preferences, timeline, must-haves.',
    icon: 'PhoneIcon',
  },
  {
    id: 'clin_step_3',
    number: '03',
    title: 'Matched to full-time roles',
    description: 'We submit you to facilities that fit. You choose which to pursue.',
    icon: 'PaperAirplaneIcon',
  },
  {
    id: 'clin_step_4',
    number: '04',
    title: 'Interview and offer support',
    description: 'Prep calls. Salary negotiation. Support through acceptance.',
    icon: 'TrophyIcon',
  },
];

export default function HowItWorksSection() {
  const [activeTab, setActiveTab] = useState<'facilities' | 'clinicians'>('facilities');

  const steps = activeTab === 'facilities' ? facilitiesSteps : cliniciansSteps;

  return (
    <section className="py-32 bg-gradient-to-b from-white via-[#f8faf9] to-white relative overflow-hidden">
      {/* Decorative Top Border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3a4d46]/20 to-transparent"></div>
      
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#4d6359] rounded-full blur-[120px]"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block mb-4">
            <span className="text-sm font-bold text-[#3a4d46] tracking-wider uppercase px-4 py-2 rounded-full bg-[#f8faf9] border border-[#3a4d46]/20">Simple Process</span>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#26342f] mb-6 tracking-tight">
            How it works
          </h2>
          <p className="text-xl text-[#3a4d46] max-w-2xl mx-auto leading-relaxed">
            Simple steps. Fast results. No surprises.
          </p>
        </div>

        {/* Enhanced Tabs */}
        <div className="flex justify-center mb-16">
          <div className="inline-flex p-2 rounded-2xl bg-white backdrop-blur-md border-2 border-[#3a4d46]/10 shadow-lg">
            <button
              onClick={() => setActiveTab('facilities')}
              className={`px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                activeTab === 'facilities' ?'bg-gradient-to-r from-[#26342f] to-[#3a4d46] text-white shadow-xl shadow-[#3a4d46]/20' :'text-[#3a4d46] hover:text-[#26342f] hover:bg-[#f8faf9]'
              }`}
            >
              For Facilities
            </button>
            <button
              onClick={() => setActiveTab('clinicians')}
              className={`px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                activeTab === 'clinicians' ?'bg-gradient-to-r from-[#26342f] to-[#3a4d46] text-white shadow-xl shadow-[#3a4d46]/20' :'text-[#3a4d46] hover:text-[#26342f] hover:bg-[#f8faf9]'
              }`}
            >
              For Clinicians
            </button>
          </div>
        </div>

        {/* Steps with Connecting Lines */}
        <div className="relative space-y-8">
          {/* Vertical Connecting Line */}
          <div className="hidden md:block absolute left-[52px] top-[80px] bottom-[80px] w-0.5 bg-gradient-to-b from-[#3a4d46]/20 via-[#3a4d46]/40 to-[#3a4d46]/20"></div>
          
          {steps.map((step, index) => (
            <div
              key={step.id}
              className="group relative"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="relative p-8 md:pl-32 rounded-3xl bg-white backdrop-blur-xl border-2 border-[#3a4d46]/10 hover:border-[#3a4d46]/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#3a4d46]/10">
                {/* Step Number Badge */}
                <div className="absolute left-8 top-1/2 -translate-y-1/2 hidden md:flex">
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#26342f] to-[#3a4d46] rounded-2xl flex items-center justify-center shadow-xl shadow-[#3a4d46]/20 group-hover:scale-110 transition-transform duration-300 border-4 border-white">
                      <span className="text-2xl font-bold text-white">{step.number}</span>
                    </div>
                    {/* Connecting Dot */}
                    {index < steps.length - 1 && (
                      <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#3a4d46]/40 rounded-full"></div>
                    )}
                  </div>
                </div>

                {/* Mobile Step Number */}
                <div className="flex md:hidden items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#26342f] to-[#3a4d46] rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-xl font-bold text-white">{step.number}</span>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-[#f8faf9] to-[#f0f4f2] rounded-xl flex items-center justify-center border border-[#3a4d46]/10">
                    <Icon name={step.icon as any} size={24} className="text-[#3a4d46]" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex items-start gap-6">
                  {/* Icon - Desktop Only */}
                  <div className="hidden md:flex flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#f8faf9] to-[#f0f4f2] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 border border-[#3a4d46]/10 shadow-md">
                      <Icon name={step.icon as any} size={28} className="text-[#3a4d46]" />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-[#26342f] mb-3 group-hover:text-[#3a4d46] transition-colors">{step.title}</h4>
                    <p className="text-lg text-[#3a4d46] leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center p-8 rounded-3xl bg-gradient-to-br from-[#f8faf9] to-white border-2 border-[#3a4d46]/10">
          <p className="text-lg text-[#3a4d46] mb-4">
            <span className="font-bold text-[#26342f]">Average time to first candidates:</span> 48 hours
          </p>
          <p className="text-lg text-[#3a4d46]">
            <span className="font-bold text-[#26342f]">Average time to hire:</span> 18 days
          </p>
        </div>
      </div>
      
      {/* Bottom Divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3a4d46]/20 to-transparent"></div>
    </section>
  );
}